import { useEffect, useState, lazy, Suspense } from "react";
import { ChunkErrorBoundary } from "./components/ChunkErrorBoundary";
import { Toaster } from "./components/ui/sonner";
import { Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import ScrollToTop from "./components/ScrollToTop";
import SchemaByRoute from "./components/SchemaByRoute";
import InstallPrompt from "./components/InstallPrompt";
import CookieConsent from "./components/CookieConsent";
import { RegionGate } from "./components/RegionGate";
import VisitorTracker from "./components/VisitorTracker";
import * as serviceWorkerRegistration from './serviceWorkerRegistration';
import { SubscriptionProvider } from './context/SubscriptionContext';
import { CurrencyProvider } from './context/CurrencyContext';
import { initGA, usePageTracking } from './utils/analytics';
import { initPerformanceMonitoring } from './utils/performance';
import { initResourcePreloading } from './utils/preload';
import { initializeUTMTracking } from './utils/utmCapture';
import './i18n';
import './styles/mobile-first.css'; // Mobile-first global utilities

// Loading component for suspense fallback
const LoadingFallback = () => (
  <div className="flex items-center justify-center min-h-screen bg-cream">
    <div className="text-center">
      <div className="inline-block h-12 w-12 animate-spin rounded-full border-4 border-solid border-sage-600 border-r-transparent"></div>
      <p className="mt-4 text-sage-700 font-medium">Loading...</p>
    </div>
  </div>
);

// Legacy /articles system has been merged into /blog — redirect old links for SEO continuity
const ArticleSlugRedirect = () => {
  const { slug } = useParams();
  if (slug && slug.startsWith('online-8-week-addiction-recovery-programme')) {
    return <Navigate to="/blog/online-8-week-addiction-recovery-programme" replace />;
  }
  return <Navigate to={slug ? `/blog/${slug}` : '/blog'} replace />;
};

// Critical pages loaded immediately (high-priority routes)
import NewLandingPage from "./pages/NewLandingPageClean";
import UnifiedLogin from "./pages/UnifiedLogin";

// Lazy load all other pages for code splitting
const MemberResourceLibrary = lazy(() => import("./pages/MemberResourceLibrary"));
const CoursesOverview = lazy(() => import("./pages/CoursesOverview"));
const CourseDetailAddict = lazy(() => import("./pages/CourseDetailAddict"));
const CourseDetailFamily = lazy(() => import("./pages/CourseDetailFamily"));
const CourseDetailStillWalkingAddict = lazy(() => import("./pages/CourseDetailStillWalkingAddict"));
const CourseDetailStillWalkingFamily = lazy(() => import("./pages/CourseDetailStillWalkingFamily"));
const CourseDetailWalkingInFreedom = lazy(() => import("./pages/CourseDetailWalkingInFreedom"));
const BuildYourTeam = lazy(() => import("./pages/BuildYourTeam"));
const VolunteerPage = lazy(() => import("./pages/VolunteerPage"));
const DailyInspirationPage = lazy(() => import("./pages/DailyInspirationPage"));
const MagicLinkVerify = lazy(() => import("./pages/MagicLinkVerify"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const RecoveryPreviewIndex = lazy(() => import("./pages/RecoveryPreviewIndex"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const ForumLeaderboard = lazy(() => import("./pages/ForumLeaderboard"));
const CommunityForums = lazy(() => import("./pages/CommunityForums"));
const MemberTestimonials = lazy(() => import("./pages/MemberTestimonials"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const CrisisPage = lazy(() => import("./pages/CrisisPage"));
const CrisisMomentPage = lazy(() => import("./pages/CrisisMomentPage"));
const CrisisMomentsIndex = lazy(() => import("./pages/CrisisMomentsIndex"));
const WhoCanSeeMyChats = lazy(() => import("./pages/WhoCanSeeMyChats"));
const QuizPage = lazy(() => import("./pages/QuizPage"));
const WhatToExpectPage = lazy(() => import("./pages/WhatToExpectPage"));
const FAQPage = lazy(() => import("./pages/site/FAQPage"));
const RelapseSupportPage = lazy(() => import("./pages/RelapseSupportPage"));
const FounderStoryPage = lazy(() => import("./pages/FounderStoryPage"));
const WhatWeBelievePage = lazy(() => import("./pages/WhatWeBelievePage"));
const JourneyRoadmap = lazy(() => import("./pages/JourneyRoadmap"));
const ChildrenOfAddictsPage = lazy(() => import("./pages/ChildrenOfAddictsPage"));
const ChildrenOfAddictionPage = lazy(() => import("./pages/ChildrenOfAddictionPage"));
const StripeSubscriptionCheckout = lazy(() => import("./pages/StripeSubscriptionCheckout"));
const PartnersDirectoryPage = lazy(() => import("./pages/PartnersDirectoryPage"));
const ForAddictsPage = lazy(() => import("./pages/ForAddictsPage"));
const ForFamiliesPage = lazy(() => import("./pages/ForFamiliesPage"));
const ForSpousesPage = lazy(() => import("./pages/ForSpousesPage"));
const ForParentsPage = lazy(() => import("./pages/ForParentsPage"));
const ForChildrenPage = lazy(() => import("./pages/ForChildrenPage"));
const ForSiblingsPage = lazy(() => import("./pages/ForSiblingsPage"));
const ForFriendsPage = lazy(() => import("./pages/ForFriendsPage"));
const ComprehensiveResourceLibrary = lazy(() => import("./pages/ComprehensiveResourceLibrary"));
const BlogIndexPage = lazy(() => import("./pages/BlogIndexPage"));
const BlogPostPage = lazy(() => import("./pages/BlogPostPage"));
const OrgSignupPage = lazy(() => import("./pages/OrgSignupPage"));
const OrgMaterialsLicencePage = lazy(() => import("./pages/OrgMaterialsLicencePage"));
const AdminOrganisations = lazy(() => import("./pages/AdminOrganisations"));
const AdminPastorInvites = lazy(() => import("./pages/AdminPastorInvites"));
const AdminLeadsPartners = lazy(() => import("./pages/AdminLeadsPartners"));
const AdminCourseAssets = lazy(() => import("./pages/AdminCourseAssets"));
const DynamicCoursePage = lazy(() => import("./pages/site/DynamicCoursePage"));
const InvitationPage = lazy(() => import("./pages/InvitationPage"));
const OrgWelcomePage = lazy(() => import("./pages/OrgWelcomePage"));
const OrgDashboardPage = lazy(() => import("./pages/OrgDashboardPage"));
const JoinOrgPage = lazy(() => import("./pages/JoinOrgPage"));
const StripeTestPage = lazy(() => import("./pages/StripeTestPage"));
const BadgesPage = lazy(() => import("./pages/BadgesPage"));
const ForOrganizationsPage = lazy(() => import("./pages/ForOrganizationsPage"));
const SafeguardingPage = lazy(() => import("./pages/SafeguardingPage"));
const NotFoundPage = lazy(() => import("./pages/NotFoundPage"));
const SiteHomePage = lazy(() => import("./pages/site/HomePage"));
const SiteFreeGuidePage = lazy(() => import("./pages/site/FreeGuidePage"));
const SiteThankYouPage = lazy(() => import("./pages/site/FreeGuidePage").then((m) => ({ default: m.ThankYouPage })));
const SiteFreeSamplePage = lazy(() => import("./pages/site/FreeGuidePage").then((m) => ({ default: m.FreeSamplePage })));
const SiteCoursesPage = lazy(() => import("./pages/site/CoursesPage"));
const SiteForChurchesPage = lazy(() => import("./pages/site/ForChurchesPage"));
const SiteChurchesPage = lazy(() => import("./pages/site/ChurchesPage"));
const SiteChurchWelcomePage = lazy(() => import("./pages/site/ChurchWelcomePage"));
const SiteLicenseeRenewedPage = lazy(() => import("./pages/site/LicenseeRenewedPage"));
const AdminChurchPartners = lazy(() => import("./pages/AdminChurchPartners"));
const AdminNotifications = lazy(() => import("./pages/AdminNotifications"));
const AdminHardship = lazy(() => import("./pages/AdminHardship"));
const AdminPrayer = lazy(() => import("./pages/AdminPrayer"));
const FreeResourcesThankYouPage = lazy(() => import("./pages/site/FreeResourcesThankYouPage"));
const SiteHardshipPage = lazy(() => import("./pages/site/HardshipPage"));
const SiteSupportPage = lazy(() => import("./pages/site/SupportPage"));
const SiteBookPastorPage = lazy(() => import("./pages/site/BookPastorSimonPage"));
const SiteTalkGuidePage = lazy(() => import("./pages/site/TalkGuidePage"));
const SiteBookSessionPage = lazy(() => import("./pages/site/BookSessionPage"));
const SiteSessionSuccessPage = lazy(() => import("./pages/site/SessionSuccessPage"));
const ReschedulePage = lazy(() => import("./pages/site/ReschedulePage"));
const AdminSessions = lazy(() => import("./pages/AdminSessions"));
const AdminGifts = lazy(() => import("./pages/AdminGifts"));
const SiteScholarshipPage = lazy(() => import("./pages/site/ScholarshipPage"));
const SiteCoursesInProgressPage = lazy(() => import("./pages/site/CoursesInProgressPage"));
const AdminSpeaking = lazy(() => import("./pages/AdminSpeaking"));
const AdminChatTranscripts = lazy(() => import("./pages/AdminChatTranscripts"));
const SiteCharterPage = lazy(() => import("./pages/site/ForChurchesPage").then((m) => ({ default: m.CharterPartnersPage })));
const LicenseeDashboard = lazy(() => import("./pages/LicenseeDashboard"));
const LicenseesDirectoryPage = lazy(() => import("./pages/LicenseesDirectoryPage"));
const LicenseeSuccessPage = lazy(() => import("./pages/LicenseeSuccessPage"));
const ChurchDirectorySignup = lazy(() => import("./pages/ChurchDirectorySignup"));
const MemberChurchSearch = lazy(() => import("./pages/MemberChurchSearch"));
const IndividualCoursePurchase = lazy(() => import("./pages/IndividualCoursePurchase"));
const SevenPrayersPage = lazy(() => import("./pages/SevenPrayersPage"));
const First24ChooserPage = lazy(() => import("./pages/First24ChooserPage"));
const First24LandingPage = lazy(() => import("./pages/First24LandingPage"));
import { TrialGate } from "./components/member/TrialGate";
const IndividualPurchaseSuccess = lazy(() => import("./pages/IndividualPurchaseSuccess"));
const LittleHeartsPage = lazy(() => import("./pages/LittleHeartsPage"));
const GrowingHeartsPage = lazy(() => import("./pages/GrowingHeartsPage"));
const AddictSignupPage = lazy(() => import("./pages/AddictSignupPage"));
const FamilySignupPage = lazy(() => import("./pages/FamilySignupPage"));
const ChurchSignupPage = lazy(() => import("./pages/ChurchSignupPage"));
const CompleteDirectoryListing = lazy(() => import("./pages/CompleteDirectoryListing"));
const ManageChurchListing = lazy(() => import("./pages/ManageChurchListing"));
const RecoveryProfileSetup = lazy(() => import("./pages/RecoveryProfileSetup"));

const YoungHeartsPage = lazy(() => import("./pages/YoungHeartsPage"));
const StillStandingPage = lazy(() => import("./pages/StillStandingPage"));
const WalkingItOutPage = lazy(() => import("./pages/WalkingItOutPage"));
const StillWatchingTheRoadPage = lazy(() => import("./pages/StillWatchingTheRoadPage"));
const TheWeightYouGrewUpWithPage = lazy(() => import("./pages/TheWeightYouGrewUpWithPage"));

// Admin pages lazy loaded (only loaded when needed)
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const AdminDevotionalPanel = lazy(() => import("./pages/AdminDevotionalPanel"));
const AdminCRM = lazy(() => import("./pages/AdminCRM"));
const AdminRecentSignups = lazy(() => import("./pages/AdminRecentSignups"));
const AdminSocialMedia = lazy(() => import("./pages/AdminSocialMedia"));
const AdminAnalyticsDashboard = lazy(() => import("./pages/AdminAnalyticsDashboard"));
const AdminReferralDashboard = lazy(() => import("./pages/AdminReferralDashboard"));
const AdminBlogPanel = lazy(() => import("./pages/AdminBlogPanel"));
const AdminDataSeeding = lazy(() => import("./pages/AdminDataSeeding"));
const ReciprocalLinksAdmin = lazy(() => import("./pages/ReciprocalLinksAdmin"));
const CourseUploadV2 = lazy(() => import("./pages/CourseUploadV2"));
const AdminEmailLog = lazy(() => import("./pages/AdminEmailLog"));
const AdminResourceDebugger = lazy(() => import("./pages/AdminResourceDebugger"));
const AdminLicenseGlobe = lazy(() => import("./pages/AdminLicenseGlobe"));
const AdminForumSeeder = lazy(() => import("./pages/AdminForumSeeder"));
const AdminTestimonials = lazy(() => import("./pages/AdminTestimonials"));
const AdminPromotional = lazy(() => import("./pages/AdminPromotional"));
const AdminRenewalEmails = lazy(() => import("./pages/AdminRenewalEmails"));
const AdminChurchApprovals = lazy(() => import("./pages/AdminChurchApprovals"));
const AdminVolunteerApplications = lazy(() => import("./pages/AdminVolunteerApplications"));
const RecoveryToServicePage = lazy(() => import("./pages/RecoveryToServicePage"));
const R2SApplicationPage = lazy(() => import("./pages/R2SApplicationPage"));
const R2SWeekOnePage = lazy(() => import("./pages/R2SWeekOnePage"));
const R2SPortalPage = lazy(() => import("./pages/R2SPortalPage"));
const R2SSupervisorPortalPage = lazy(() => import("./pages/R2SSupervisorPortalPage"));
const AdminR2SApplications = lazy(() => import("./pages/AdminR2SApplications"));
const R2SRegisterOrganizationPage = lazy(() => import("./pages/R2SRegisterOrganizationPage"));
const R2SRegisterSuccessPage = lazy(() => import("./pages/R2SRegisterSuccessPage"));
const R2SVerifyRegisterPage = lazy(() => import("./pages/R2SVerifyRegisterPage"));
const R2SOrgStatusPage = lazy(() => import("./pages/R2SOrgStatusPage"));
const R2SFaqCandidatesPage = lazy(() => import("./pages/R2SFaqCandidatesPage"));
const R2SFaqOrganizationsPage = lazy(() => import("./pages/R2SFaqOrganizationsPage"));
const { R2SLoginPage, R2SSetPasswordPage } = { R2SLoginPage: lazy(() => import("./pages/R2SAuthPages").then(m => ({ default: m.R2SLoginPage }))), R2SSetPasswordPage: lazy(() => import("./pages/R2SAuthPages").then(m => ({ default: m.R2SSetPasswordPage }))) };
const AdminQuotesManager = lazy(() => import("./pages/AdminQuotesManager"));
const AdminLiveVisitors = lazy(() => import("./pages/AdminLiveVisitors"));
const AdminConcernReports = lazy(() => import("./pages/AdminConcernReports"));
const ArticleEditor = lazy(() => import("./pages/ArticleEditor"));
const LocalSEOPage = lazy(() => import("./pages/LocalSEOPage"));
const AskUsPage = lazy(() => import("./pages/AskUsPage"));
const DonationSuccess = lazy(() => import("./pages/DonationSuccess"));
const AdminSocialMediaLibrary = lazy(() => import("./pages/AdminSocialMediaLibrary"));
const AdminSocialMediaGenerator = lazy(() => import("./pages/AdminSocialMediaGenerator"));
const MemesMediaAdmin = lazy(() => import("./pages/MemesMediaAdmin"));
const AdminDevotionalMigration = lazy(() => import("./pages/AdminDevotionalMigration"));
const FamilyDashboardPage = lazy(() => import("./pages/FamilyDashboardPage"));
const FamilyInviteAcceptPage = lazy(() => import("./pages/FamilyInviteAcceptPage"));
const PartnerInviteAcceptPage = lazy(() => import("./pages/PartnerInviteAcceptPage"));
const ExplosionPlanDashboard = lazy(() => import("./pages/ExplosionPlanDashboard"));
const ContentCalendar = lazy(() => import("./pages/ContentCalendar"));
const ContentAnalytics = lazy(() => import("./pages/ContentAnalytics"));

// Keep essential components eagerly loaded
const CustomerPortal = lazy(() => import("./components/CustomerPortal"));
const AuthCallback = lazy(() => import("./components/AuthCallback"));
const GroupManagement = lazy(() => import("./components/GroupManagement"));
const DebugEnrollment = lazy(() => import("./pages/DebugEnrollment"));

// Styles
import "./styles/recovery-design-system.css";
import "./App.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;


function VerifyAlias() {
  const location = useLocation();
  return <Navigate to={`/recovery-to-service/verify${location.search}`} replace />;
}

function App() {
  const location = useLocation();
  const isBackstageArea = /^\/(admin|member|support-team)(\/|$)/.test(location.pathname);
  const canonicalPath = location.pathname.replace(/\/+$/, '') || '/';

  // Exactly one canonical per page: pages with their own <SEO> win; otherwise fall back to the current URL.
  useEffect(() => {
    const apply = () => {
      const links = Array.from(document.head.querySelectorAll('link[rel="canonical"]'));
      const pageLevel = links.filter((l) => !l.hasAttribute('data-fallback-canonical'));
      links.filter((l) => l.hasAttribute('data-fallback-canonical')).forEach((l) => l.remove());
      pageLevel.slice(1).forEach((l) => l.remove());
      if (pageLevel.length === 0) {
        const link = document.createElement('link');
        link.rel = 'canonical';
        link.href = `https://www.christianfamilyrecovery.com${canonicalPath === '/' ? '/' : canonicalPath}`;
        link.setAttribute('data-fallback-canonical', '1');
        document.head.appendChild(link);
      }
    };
    apply();
    const t1 = setTimeout(apply, 400);
    const t2 = setTimeout(apply, 1500);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [canonicalPath]);
  const isPrivateArea = isBackstageArea || /^\/(customer|licensee|r2s|org\/|join\/|verify|login|signup\/success|recovery\/login|recovery-to-service\/(portal|register|verify)|community|forums|badges|build-your-team|support-team)/.test(location.pathname);

  // Initialize Google Analytics
  useEffect(() => {
    const GA_MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID;
    if (GA_MEASUREMENT_ID) {
      initGA(GA_MEASUREMENT_ID);
    }
  }, []);

  // Track page views
  usePageTracking();

  // Initialize UTM tracking (capture marketing attribution)
  useEffect(() => {
    initializeUTMTracking();
  }, []);

  // Register service worker for PWA
  useEffect(() => {
    serviceWorkerRegistration.register();
  }, []);

  // Initialize performance monitoring (only in production)
  useEffect(() => {
    if (process.env.NODE_ENV === 'production') {
      initPerformanceMonitoring();
    }
  }, []);

  // Initialize resource preloading
  useEffect(() => {
    initResourcePreloading();
  }, []);


  return (
    <RegionGate>
    <SubscriptionProvider>
    <CurrencyProvider>
      <Helmet defaultTitle="Christian Family Recovery | The Complete Christian Addiction Recovery Service for Individuals, Families, Churches & Rehab Centres" titleTemplate="%s">
        {isPrivateArea && <meta name="robots" content="noindex, nofollow" />}
      </Helmet>
      {/* Scroll to top on route change */}
      <ScrollToTop />
      <SchemaByRoute />

      {/* Testimonial ticker retired in the one-time-purchase pivot */}

      <ChunkErrorBoundary>
      <Suspense fallback={<LoadingFallback />}>
        <main id="main-content" data-testid="main-content">
        <Routes>
        {/* Main Recovery Routes */}
        <Route path="/" element={<SiteHomePage />} />
        <Route path="/legacy-home" element={<NewLandingPage />} />
        <Route path="/es" element={<Navigate to="/" replace />} />
        <Route path="/free-guide" element={<SiteFreeGuidePage />} />
        <Route path="/thank-you" element={<SiteThankYouPage />} />
        <Route path="/free-sample" element={<SiteFreeSamplePage />} />
        <Route path="/free-resources/thank-you" element={<FreeResourcesThankYouPage />} />
        <Route path="/for-churches" element={<SiteForChurchesPage />} />
        <Route path="/churches" element={<SiteChurchesPage />} />
        <Route path="/churches/welcome/:token" element={<SiteChurchWelcomePage />} />
        <Route path="/charter-partners" element={<SiteCharterPage />} />
        <Route path="/my-account" element={<Navigate to="/login" replace />} />
        <Route path="/recovery" element={<NewLandingPage />} />
        <Route path="/login" element={<UnifiedLogin />} />
        <Route path="/signup" element={<Navigate to="/courses" replace />} />
        <Route path="/es/signup" element={<Navigate to="/courses" replace />} />
        <Route path="/signup-legacy" element={<Navigate to="/courses" replace />} />
        <Route path="/signup/addict" element={<AddictSignupPage />} />
        <Route path="/signup/family" element={<FamilySignupPage />} />
        <Route path="/signup/church" element={<ChurchSignupPage />} />
        <Route path="/complete-directory-listing" element={<CompleteDirectoryListing />} />
        <Route path="/recovery/login" element={<UnifiedLogin />} />
        <Route path="/customer/login" element={<UnifiedLogin />} />
        <Route path="/auth/verify" element={<MagicLinkVerify />} />
        
        {/* Course Detail Pages */}
        <Route path="/courses" element={<SiteCoursesPage />} />
        <Route path="/courses-legacy" element={<CoursesOverview />} />
        <Route path="/courses/walking-it-out-addict" element={<CourseDetailAddict />} />
        <Route path="/courses/walking-it-out-family" element={<CourseDetailFamily />} />
        <Route path="/courses/still-walking-it-out-addict" element={<CourseDetailStillWalkingAddict />} />
        <Route path="/courses/still-walking-it-out-family" element={<CourseDetailStillWalkingFamily />} />
        <Route path="/courses/walking-in-freedom" element={<CourseDetailWalkingInFreedom />} />
        
        {/* Member Area */}
        <Route path="/member/library" element={<TrialGate><MemberResourceLibrary /></TrialGate>} />
        <Route path="/member/support" element={<Navigate to="/member/library" replace />} />
        <Route path="/member/church-listing" element={<ManageChurchListing />} />
        <Route path="/member/recovery-profile" element={<RecoveryProfileSetup />} />
        <Route path="/member/courses" element={<MemberResourceLibrary />} />
        <Route path="/member/profile" element={<TrialGate><MemberResourceLibrary /></TrialGate>} />
        <Route path="/build-your-team" element={<BuildYourTeam />} />
        <Route path="/support-team" element={<Navigate to="/member/library" replace />} />
        <Route path="/member/inspiration" element={<TrialGate><DailyInspirationPage /></TrialGate>} />
        <Route path="/member/welcome" element={<Navigate to="/member/library" replace />} />
        <Route path="/member/settings" element={<Navigate to="/member/library?tab=profile" replace />} />
        <Route path="/forums/leaderboard" element={<Navigate to="/member/library" replace />} />

        <Route path="/member/community" element={<Navigate to="/member/library" replace />} />
        <Route path="/member/badges" element={<TrialGate><BadgesPage /></TrialGate>} />
        <Route path="/member/testimonials" element={<MemberTestimonials />} />
        <Route path="/member/referrals" element={<Navigate to="/member/library" replace />} />
        <Route path="/referrals" element={<Navigate to="/member/library" replace />} />
        <Route path="/member/family" element={<TrialGate><FamilyDashboardPage /></TrialGate>} />
        <Route path="/family-invite/:token" element={<FamilyInviteAcceptPage />} />
        <Route path="/partner-invite/:token" element={<PartnerInviteAcceptPage />} />
        <Route path="/badges" element={<BadgesPage />} />
        <Route path="/community" element={<Navigate to="/member/library" replace />} />
        <Route path="/resources" element={<ComprehensiveResourceLibrary />} />
        <Route path="/group-management" element={<GroupManagement />} />
        <Route path="/member/group" element={<GroupManagement />} />
        
        {/* Legacy Customer Portal */}
        <Route path="/customer/portal" element={<CustomerPortal />} />
        <Route path="/customer/reset-password" element={<ResetPassword />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/customer/forgot-password" element={<ForgotPassword />} />
        
        {/* Auth Callback */}
        <Route path="/auth/callback" element={<AuthCallback />} />
        
        {/* Preview Index */}
        <Route path="/recovery-preview" element={<RecoveryPreviewIndex />} />
        
        {/* Terms & Privacy */}
        <Route path="/terms" element={<TermsOfService />} />
        <Route path="/debug-enrollment" element={<DebugEnrollment />} />
        <Route path="/privacy" element={<TermsOfService />} />
        
        {/* Contact Page */}
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/volunteer" element={<VolunteerPage />} />
        
        {/* New Resource Pages */}
        <Route path="/ai-companions" element={<Navigate to="/courses" replace />} />
        <Route path="/crisis" element={<CrisisPage />} />
        <Route path="/es/crisis" element={<CrisisPage />} />
        <Route path="/what-to-expect" element={<WhatToExpectPage />} />
        <Route path="/journey" element={<JourneyRoadmap />} />
        <Route path="/faq" element={<FAQPage />} />
        <Route path="/es/faq" element={<FAQPage />} />
        <Route path="/relapse-support" element={<RelapseSupportPage />} />
        <Route path="/pricing" element={<Navigate to="/courses" replace />} />
        <Route path="/es/pricing" element={<Navigate to="/courses" replace />} />
        <Route path="/pricing-legacy" element={<Navigate to="/courses" replace />} />
        <Route path="/pricing-old" element={<Navigate to="/courses" replace />} />
        <Route path="/donation/success" element={<DonationSuccess />} />
        <Route path="/founder-story" element={<FounderStoryPage />} />
        <Route path="/what-we-believe" element={<WhatWeBelievePage />} />
        
        {/* Persona Pages */}
        <Route path="/for-addicts" element={<ForAddictsPage />} />
        <Route path="/for-families" element={<ForFamiliesPage />} />
        <Route path="/for-spouses" element={<ForSpousesPage />} />
        <Route path="/for-parents" element={<ForParentsPage />} />
        <Route path="/for-children" element={<ForChildrenPage />} />
        <Route path="/for-siblings" element={<ForSiblingsPage />} />
        <Route path="/for-friends" element={<ForFriendsPage />} />
        <Route path="/for-organizations" element={<Navigate to="/for-churches" replace />} />
        <Route path="/for-organizations-legacy" element={<ForOrganizationsPage />} />
        <Route path="/what-we-offer" element={<Navigate to="/courses" replace />} />
        <Route path="/what-we-offer-legacy" element={<Navigate to="/courses" replace />} />
        <Route path="/safeguarding" element={<SafeguardingPage />} />
        <Route path="/how-it-works" element={<Navigate to="/courses" replace />} />
        <Route path="/services" element={<Navigate to="/courses" replace />} />

        <Route path="/for-organizations/licence" element={<Navigate to="/for-churches" replace />} />
        <Route path="/for-organizations/licence-legacy" element={<OrgMaterialsLicencePage />} />
        <Route path="/for-organizations/license" element={<Navigate to="/for-organizations/licence" replace />} />
        <Route path="/for-churches-program" element={<Navigate to="/for-churches" replace />} />
        <Route path="/church-signup" element={<ChurchDirectorySignup />} />
        <Route path="/church-directory" element={<MemberChurchSearch />} />
        
        {/* Licensee Portal */}
        <Route path="/licensee/dashboard" element={<LicenseeDashboard />} />
        <Route path="/licensees-directory" element={<LicenseesDirectoryPage />} />
        <Route path="/licensee/success" element={<LicenseeSuccessPage />} />
        <Route path="/licensee/renewed" element={<SiteLicenseeRenewedPage />} />
        
        {/* Admin Panel */}
        <Route path="/admin" element={<AdminDashboard />} />
        <Route path="/admin/dashboard" element={<AdminDashboard />} />
        <Route path="/admin/devotionals" element={<AdminDevotionalPanel />} />
        <Route path="/admin/migrate-devotionals" element={<AdminDevotionalMigration />} />
        <Route path="/admin/crm" element={<AdminCRM />} />
        <Route path="/admin/recent-signups" element={<AdminRecentSignups />} />
        <Route path="/admin/social-media" element={<AdminSocialMedia />} />
        <Route path="/admin/social-media/promotional" element={<AdminPromotional />} />
        <Route path="/admin/social-media/library" element={<AdminSocialMediaLibrary />} />
        <Route path="/admin/social-media/generator" element={<AdminSocialMediaGenerator />} />
        <Route path="/admin/memes-media" element={<MemesMediaAdmin />} />
        <Route path="/admin/content-calendar" element={<ContentCalendar />} />
        <Route path="/admin/content-analytics" element={<ContentAnalytics />} />
        <Route path="/admin/testimonials" element={<AdminTestimonials />} />
        <Route path="/admin/analytics" element={<AdminAnalyticsDashboard />} />
        <Route path="/admin/referrals" element={<AdminReferralDashboard />} />
        <Route path="/admin/explosion-plan" element={<ExplosionPlanDashboard />} />
        <Route path="/admin/blog" element={<AdminBlogPanel />} />
        <Route path="/admin/reciprocal-links" element={<ReciprocalLinksAdmin />} />
        <Route path="/admin/seed-data" element={<AdminDataSeeding />} />
        <Route path="/admin/seed-forums" element={<AdminForumSeeder />} />
        <Route path="/admin/renewal-emails" element={<AdminRenewalEmails />} />
        <Route path="/admin/courses-v2" element={<CourseUploadV2 />} />
        <Route path="/admin/email-log" element={<AdminEmailLog />} />
        <Route path="/admin/debug" element={<AdminResourceDebugger />} />
        <Route path="/admin/globe" element={<AdminLicenseGlobe />} />
        <Route path="/admin/church-approvals" element={<AdminChurchApprovals />} />
        <Route path="/admin/course-assets" element={<AdminCourseAssets />} />
        <Route path="/admin/volunteer-applications" element={<AdminVolunteerApplications />} />
        <Route path="/recovery-to-service" element={<RecoveryToServicePage />} />
        <Route path="/recovery-to-service/apply" element={<R2SApplicationPage />} />
        <Route path="/recovery-to-service/week-one" element={<R2SWeekOnePage />} />
        <Route path="/recovery-to-service/register" element={<R2SRegisterOrganizationPage />} />
        <Route path="/recovery-to-service/register/success" element={<R2SRegisterSuccessPage />} />
        <Route path="/recovery-to-service/verify" element={<R2SVerifyRegisterPage />} />
        <Route path="/verify" element={<VerifyAlias />} />
        <Route path="/recovery-to-service/organizations/:orgId" element={<R2SOrgStatusPage />} />
        <Route path="/recovery-to-service/faq" element={<R2SFaqCandidatesPage />} />
        <Route path="/recovery-to-service/faq-for-organisations" element={<R2SFaqOrganizationsPage />} />
        <Route path="/r2s/login" element={<R2SLoginPage />} />
        <Route path="/r2s/set-password" element={<R2SSetPasswordPage />} />
        <Route path="/r2s/portal" element={<R2SPortalPage />} />
        <Route path="/r2s/supervisor/login" element={<R2SLoginPage role="supervisor" />} />
        <Route path="/r2s/supervisor/set-password" element={<R2SSetPasswordPage />} />
        <Route path="/r2s/supervisor/portal" element={<R2SSupervisorPortalPage />} />
        <Route path="/admin/recovery-to-service" element={<AdminR2SApplications />} />
        <Route path="/admin/quotes" element={<AdminQuotesManager />} />
        <Route path="/admin/subscriptions" element={<Navigate to="/admin" replace />} />
        <Route path="/admin/live-visitors" element={<AdminLiveVisitors />} />
        <Route path="/admin/concern-reports" element={<AdminConcernReports />} />
        <Route path="/admin/articles" element={<Navigate to="/admin/blog" replace />} />
        <Route path="/admin/organisations" element={<AdminOrganisations />} />
        <Route path="/admin/pastor-invites" element={<AdminPastorInvites />} />
        <Route path="/admin/leads-partners" element={<AdminLeadsPartners />} />
        <Route path="/admin/voice-settings" element={<Navigate to="/admin" replace />} />
        <Route path="/admin/church-partners" element={<AdminChurchPartners />} />
        <Route path="/admin/notifications" element={<AdminNotifications />} />
        <Route path="/admin/hardship" element={<AdminHardship />} />
        <Route path="/admin/prayer" element={<AdminPrayer />} />
        <Route path="/hardship" element={<SiteHardshipPage />} />
        <Route path="/support" element={<SiteSupportPage />} />
        <Route path="/book-pastor-simon" element={<SiteBookPastorPage />} />
        <Route path="/guide/:code" element={<SiteTalkGuidePage />} />
        <Route path="/book-a-session" element={<SiteBookSessionPage />} />
        <Route path="/sessions/success" element={<SiteSessionSuccessPage />} />
        <Route path="/sessions/reschedule/:token" element={<ReschedulePage />} />
        <Route path="/admin/sessions" element={<AdminSessions />} />
        <Route path="/admin/gifts" element={<AdminGifts />} />
        <Route path="/scholarship" element={<SiteScholarshipPage />} />
        <Route path="/courses-in-progress" element={<SiteCoursesInProgressPage />} />
        <Route path="/admin/speaking" element={<AdminSpeaking />} />
        <Route path="/admin/chat-transcripts" element={<AdminChatTranscripts />} />
        <Route path="/invitation/:slug" element={<InvitationPage />} />
        <Route path="/admin/articles/edit/:id" element={<ArticleEditor />} />
        
        {/* Public Articles — merged into /blog, redirect legacy links */}
        <Route path="/articles" element={<Navigate to="/blog" replace />} />
        <Route path="/recovery-to-service/organisations/faq" element={<Navigate to="/recovery-to-service/faq-for-organisations" replace />} />
        <Route path="/articles/:slug" element={<ArticleSlugRedirect />} />
        
        {/* Checkout & Counsellor */}
        <Route path="/checkout" element={<StripeSubscriptionCheckout />} />
        <Route path="/stripe-test" element={<StripeTestPage />} />
        <Route path="/counsellors" element={<Navigate to="/courses" replace />} />
        
        {/* Individual Course Purchase */}
        <Route path="/buy-courses" element={<IndividualCoursePurchase />} />
        <Route path="/seven-prayers" element={<SevenPrayersPage />} />
        <Route path="/first-24-hours" element={<First24ChooserPage />} />
        <Route path="/first-24-hours/:track" element={<First24LandingPage />} />
        <Route path="/help" element={<CrisisMomentsIndex />} />
        <Route path="/help/:slug" element={<CrisisMomentPage />} />
        <Route path="/es/help/:slug" element={<CrisisMomentPage />} />
        <Route path="/who-can-see-my-chats" element={<WhoCanSeeMyChats />} />
        <Route path="/quiz" element={<QuizPage />} />
        <Route path="/individual-license/success" element={<IndividualPurchaseSuccess />} />
        
        {/* Course Sales Pages */}
        <Route path="/course/little-hearts" element={<LittleHeartsPage />} />
        <Route path="/course/growing-hearts" element={<GrowingHeartsPage />} />
        <Route path="/course/young-hearts" element={<YoungHeartsPage />} />
        <Route path="/course/still-standing" element={<StillStandingPage />} />
        <Route path="/course/walking-it-out" element={<WalkingItOutPage />} />
        <Route path="/course/still-watching-the-road" element={<StillWatchingTheRoadPage />} />
        <Route path="/course/the-weight-you-grew-up-with" element={<TheWeightYouGrewUpWithPage />} />
        <Route path="/course/children-of-addiction" element={<ChildrenOfAddictionPage />} />
        <Route path="/course/out-of-the-shadows" element={<Navigate to="/courses" replace />} />
        <Route path="/course/:slug" element={<DynamicCoursePage />} />
        
        {/* Sales page routes without /course prefix */}
        <Route path="/little-hearts" element={<LittleHeartsPage />} />
        <Route path="/growing-hearts" element={<GrowingHeartsPage />} />
        <Route path="/young-hearts" element={<YoungHeartsPage />} />
        <Route path="/still-standing" element={<StillStandingPage />} />
        <Route path="/es/still-standing" element={<StillStandingPage />} />
        <Route path="/walking-it-out" element={<WalkingItOutPage />} />
        <Route path="/still-watching-the-road" element={<StillWatchingTheRoadPage />} />
        <Route path="/the-weight-you-grew-up-with" element={<TheWeightYouGrewUpWithPage />} />
        <Route path="/children-of-addiction" element={<ChildrenOfAddictionPage />} />
        
        {/* Partners Directory */}
        <Route path="/partners" element={<PartnersDirectoryPage />} />
        
        {/* Blog */}
        <Route path="/blog" element={<BlogIndexPage />} />
        <Route path="/blog/:slug" element={<BlogPostPage />} />
        <Route path="/org/signup" element={<OrgSignupPage />} />
        <Route path="/org/welcome" element={<OrgWelcomePage />} />
        <Route path="/org/dashboard" element={<OrgDashboardPage />} />
        <Route path="/join/:code" element={<JoinOrgPage />} />
        
        {/* Local SEO Pages */}
        <Route path="/locations/:locationSlug" element={<LocalSEOPage />} />
        
        {/* Ask Us — public Ministry Assistant chatbot, full page */}
        <Route path="/ask" element={<AskUsPage />} />
        
        {/* Catch-all: real Not Found page (noindex) instead of silently showing the homepage */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
        </main>
      </Suspense>
      </ChunkErrorBoundary>
      
      {/* PWA Install Prompt */}
      <InstallPrompt />
      
      {/* GDPR Cookie Consent */}
      <CookieConsent />
      
      {/* Visitor Tracker (tracks new sessions for admin notifications) */}
      <VisitorTracker />
      
      {/* Site-wide visitor chat (public pages only) with WhatsApp hand-off to Pastor Simon */}
      {/* AI SiteChatWidget retired in favour of Conferbot live chat (component kept in components/SiteChatWidget.jsx) */}
      
      <Toaster position="top-right" />
    </CurrencyProvider>
    </SubscriptionProvider>
    </RegionGate>
  );
}

export default App;
